// Small hand-rolled focus-trap helpers for modal dialogs (no new deps).
//
// Usage inside a dialog's existing keydown handler:
//   const previouslyFocused = captureFocus();
//   ...
//   if (e.key === 'Tab' && panelRef.current) trapFocus(panelRef.current, e);
//   ...
//   // on close/unmount:
//   restoreFocus(previouslyFocused);

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Focusable elements within `container`, in DOM order. No visibility
 * filtering (`display: none` / `visibility: hidden` / zero-size ancestors
 * aren't excluded) - callers that pass in arbitrary children (e.g.
 * ModelDetailsModal's `extraActions`) should keep any conditionally-hidden
 * interactive content genuinely removed from the DOM rather than just
 * visually hidden, or it will still take part in the trap/tab order.
 */
export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Keeps Tab/Shift+Tab focus cycling within `container`. Call from a keydown
 * handler with the raw KeyboardEvent; no-ops for any key other than Tab.
 * Wraps last -> first (Tab) and first -> last (Shift+Tab); also re-anchors
 * focus into the container if it somehow escaped.
 */
export function trapFocus(container: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;

  const focusable = getFocusableElements(container);
  if (focusable.length === 0) {
    e.preventDefault();
    container.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement as HTMLElement | null;

  if (e.shiftKey) {
    if (active === first || !active || !container.contains(active)) {
      e.preventDefault();
      last.focus();
    }
  } else if (active === last || !active || !container.contains(active)) {
    e.preventDefault();
    first.focus();
  }
}

/** Capture the currently-focused element so it can be restored later. */
export function captureFocus(): HTMLElement | null {
  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

/** Restore focus to a previously-captured element, if it's still in the document. */
export function restoreFocus(el: HTMLElement | null): void {
  if (el && document.contains(el)) {
    el.focus();
  }
}
